import { Component, Emit, Mixins, Prop, Watch } from 'vue-property-decorator'
import { TranslateResult } from 'vue-i18n'
import { sumBy } from 'lodash'
import { BAlert, BButton, BFormRadioGroup, BModal, VBTooltip } from 'bootstrap-vue'
import { DateMixin, NumberMixin, Spinner } from '@kwixeo/interface-vue'
import { KFormSpinner } from '@kwixeo/form-vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { mapGetters } from 'vuex'

@Component({
  components: {
    BButton, BModal, BFormRadioGroup, BAlert,
    Spinner,
    KFormSpinner,
    FontAwesomeIcon
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  computed: {
    ...mapGetters('subscriptions', ['hasModule'])
  }
})
export default class LotSelect extends Mixins(DateMixin, NumberMixin) {
  hasModule!: (key: string) => boolean

  @Prop({ default: () => [] }) value!: KwixeoCatalog.UsedLots
  @Prop({ required: true }) product!: KwixeoCatalog.Product
  @Prop({ default: () => [] }) usedLots!: KwixeoCatalog.UsedLots
  @Prop({ required: true }) quantity!: number
  @Prop({ default: 'primary' }) variant!: string
  @Prop({ default: null }) size!: string
  @Prop({ default: false }) disabled!: boolean
  @Prop({ default: null }) label!: string|TranslateResult
  @Prop({ default: null }) location!: KwixeoCatalog.StockLocation|null
  @Prop({ default: 0 }) autoSelect!: number
  @Prop({ default: () => true }) lotsFilter!: (lot: KwixeoCatalog.UsedLot) => boolean

  private loading = true
  private loaded = false
  private modalState = false
  private selected: KwixeoCatalog.UsedLots = []
  private autoSelectionMode = 1
  private lots: KwixeoCatalog.ProductLots = []

  @Watch('quantity', { immediate: true })
  private async watchQuantity(): Promise<void> {
    if (this.autoSelect === 0) {
      return
    }

    if (!this.loaded) {
      this.watchSelected(this.value)
      await this.getDatas()
    }

    if (this.autoSelect === 2 && this.allLots.length > 1) {
      return
    }
    this.autoSelection()
  }

  get filteredUsedLots (): KwixeoCatalog.UsedLots {
    return (this.usedLots ?? []).filter(this.lotsFilter)
  }

  get computedLabel (): string|TranslateResult {
    return this.label ?? 'Sélectionner un lot'
  }

  // Return available lots
  get allLots (): KwixeoCatalog.ProductLots {
    const lots: KwixeoCatalog.ProductLots = JSON.parse(JSON.stringify(this.lots))
    this.filteredUsedLots.forEach(lot => {
      const used = this.selected.find(use => use.lot.id === lot.lot.id)
      if (!used) {
        const unusedLot = JSON.parse(JSON.stringify(lot.lot))
        unusedLot.stock_quantity = this.number(unusedLot.stock_quantity) + this.number(lot.quantity)

        const existIndex = lots.findIndex(val => val.id === unusedLot.id)
        if (existIndex >= 0) {
          lots.splice(existIndex, 1)
        }

        lots.push(unusedLot)
      }
    })

    return lots
  }

  get unselectedLots (): KwixeoCatalog.ProductLots {
    return this.allLots.filter(lot => !this.selected.find(select => select.lot.id === lot.id))
  }

  get totalSelectedQuantity (): number {
    return this.roundNumber(sumBy(this.selected, value => this.number(value.quantity)), 2)
  }

  get cantSelectLot (): boolean {
    return this.totalSelectedQuantity >= this.quantity
  }

  get tooMuchQuantity (): boolean {
    return this.totalSelectedQuantity > this.quantity
  }

  get stockQuantityAvailable (): (lot: KwixeoCatalog.UsedLot) => number {
    return lot => {
      const usedLot = this.value.find(used => used.lot?.id === lot.lot.id)
      return this.number(lot.lot.stock_quantity) + (usedLot ? this.number(usedLot.quantity) : 0)
    }
  }

  get availableStockQuantityAvailable (): (lot: KwixeoCatalog.ProductLot) => number {
    return lot => {
      const usedLot = this.value.find(used => used.lot?.id === lot.id)
      return this.number(lot.stock_quantity) + (usedLot ? this.number(usedLot.quantity) : 0)
    }
  }

  @Watch('value', { immediate: true })
  private watchSelected (value: KwixeoCatalog.UsedLots): void {
    this.selected = JSON.parse(JSON.stringify(value.filter(this.lotsFilter)))
  }

  @Watch('modalState')
  private async watchModalState (state: boolean): Promise<void> {
    if (state) {
      this.watchSelected(this.value)
      await this.getDatas()
    }
  }

  // Fetch lots from api
  private async getDatas (): Promise<void> {
    this.loading = true

    const filter: KwixeoInterface.GenericObject = {
      where_product: this.product.id,
      only_in_stock: true
    }
    if (this.hasModule('stock-locations') && this.location) {
      filter.stock_location = this.location.id
    }

    return await this.$kwCatalog.axios.get('/catalog/stock/lot', {
        params: {
          per_page: 500,
          sort: 'expire_at',
          filter
        }
      })
      .then(response => {
        this.lots = response.data.data
        this.loading = false
        this.loaded = true
      })
      .catch(error => {
        throw new Error(error)
      })
  }

  // Select automaticaly the lots
  private autoSelection (): void {
    let lots = Array.from(this.allLots)
    const selected: KwixeoCatalog.UsedLots = []
    let quantity = this.number(this.quantity)

    this.filteredUsedLots.every(lot => {
      let lotQty = this.number(lot.quantity)
      if (lotQty > quantity) {
        lotQty = quantity
      }

      if (lotQty > 0) {
        selected.push({
          lot: lot.lot,
          quantity: lotQty
        })
      }

      quantity -= lotQty
      return quantity > 0
    })

    if (quantity > 0) {
      if (this.product.stock_tracking_mode === 1) {
        lots = lots.sort((a, b) => new Date(a.expire_at as string).getTime() - new Date(b.expire_at as string).getTime())
        if (this.autoSelectionMode === 2) {
          const lotsMin = lots.filter(lot => this.number(lot.stock_quantity) < quantity)
          lots = lots.filter(lot => this.number(lot.stock_quantity) >= quantity)
          lots.push(...lotsMin)
        }
      }

      lots.every(lot => {
        let lotQty = this.number(lot.stock_quantity)
        if (lotQty > quantity) {
          lotQty = quantity
        }

        if (lotQty > 0) {
          selected.push({
            lot,
            quantity: lotQty
          })
        }

        quantity -= lotQty
        return quantity > 0
      })
    }

    this.selected = selected
    this.validSelection()
  }

  private selectLot (lot: KwixeoCatalog.ProductLot): void {
    let stayQty = this.roundNumber(this.quantity - this.totalSelectedQuantity, 5)
    if (stayQty > lot.stock_quantity) {
      stayQty = this.number(lot.stock_quantity)
    }

    const index = this.selected.findIndex(val => val.lot.id === lot.id)
    if (index >= 0) {
      this.selected[index].quantity += stayQty
    } else {
      this.selected.push({
        lot,
        quantity: stayQty
      })
    }
  }

  @Emit('input')
  private validSelection (): KwixeoCatalog.UsedLots {
    this.modalState = false
    return JSON.parse(JSON.stringify(this.selected))
  }

  // Remove a selected lot
  private deleteSelection (index: number): void {
    this.selected.splice(index, 1)
  }
}
